// pages/ExamsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import ExamList from "../components/exam/ExamList";
import EmptyState from "../components/exam/EmptyState";
import ExamCategoryExplorer from "../components/exam/ExamCategoryExplorer";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../utils/httpRequest";
import { useUserStore } from "../stores/useUserStore";

const ExamsPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const [path, setPath] = useState([]); // [{ _id, title }]
  const [allCategories, setAllCategories] = useState([]);
  const [currentCategories, setCurrentCategories] = useState([]);
  const [exams, setExams] = useState([]);

  // لودینگ/ارور
  const [catsLoading, setCatsLoading] = useState(false);
  const [catsError, setCatsError] = useState(null);
  const [examsLoading, setExamsLoading] = useState(false);
  const [examsError, setExamsError] = useState(null);
  const [examCounts, setExamCounts] = useState(null); // اگر بعداً لازم شد

  const { subscriptions, fetchSubscriptions } = useUserStore();
  const user = useUserStore((s) => s.user);

  useEffect(() => {
    fetchSubscriptions(); // هنگام ورود به صفحه
  }, [fetchSubscriptions]);

  // --- دریافت همهٔ دسته‌بندی‌ها ---
  useEffect(() => {
    let abort = false;
    (async () => {
      setCatsLoading(true);
      setCatsError(null);
      try {
        const resp = await httpRequest(
          "GET",
          "/category",
          {},
          { "x-access-token": user?.token }
        );
        const cats = resp?.data?.data || resp?.data || [];
        if (abort) return;

        setAllCategories(cats);

        // ریشه‌ها (level === 1 یا parent=null)
        const roots = cats.filter(
          (c) => c?.level === 1 || c?.parent == null || c?.parent === "null"
        );
        setCurrentCategories(roots);
      } catch (e) {
        if (!abort)
          setCatsError(e?.response?.data?.message || e.message || "Error");
      } finally {
        if (!abort) setCatsLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [user?.token]);

  // --- گرفتن آزمون‌های یک کتگوری برگ ---
  const fetchExamsForCategory = async (categoryId) => {
    setExamsLoading(true);
    setExamsError(null);
    try {
      const resp = await httpRequest(
        "GET",
        `/exam?published=true&category=${encodeURIComponent(categoryId)}`,
        {},
        { "x-access-token": user?.token }
      );
      const list = resp?.data?.data || resp?.data || [];
      setExams(list);
    } catch (e) {
      setExams([]);
      setExamsError(e?.response?.data?.message || e.message || "Error");
    } finally {
      setExamsLoading(false);
    }
  };

  // --- انتخاب دسته ---
  const handleSelectCategory = (cat) => {
    const newPath = [...path, { _id: cat._id, title: cat.title }];
    setPath(newPath);

    const children = allCategories.filter(
      (c) => String(c.parent) === String(cat._id)
    );

    if (children.length > 0) {
      // هنوز به برگ نرسیدیم
      setCurrentCategories(children);
      setExams([]);
    } else {
      // برگ/سطح آخر → آزمون‌ها را بگیر
      setCurrentCategories([]);
      fetchExamsForCategory(cat._id);
    }
  };

  // --- ناوبری مسیر (breadcrumb) ---
  const handlePathClick = (index) => {
    if (index === -1) {
      // بازگشت به ریشه
      const roots = allCategories.filter(
        (c) => c?.level === 1 || c?.parent == null || c?.parent === "null"
      );
      setPath([]);
      setCurrentCategories(roots);
      setExams([]);
      setExamsError(null);
      return;
    }

    const newPath = path.slice(0, index + 1);
    setPath(newPath);

    const lastCat = newPath[newPath.length - 1];
    const children = allCategories.filter(
      (c) => String(c.parent) === String(lastCat._id)
    );

    if (children.length > 0) {
      setCurrentCategories(children);
      setExams([]);
      setExamsError(null);
    } else {
      setCurrentCategories([]);
      fetchExamsForCategory(lastCat._id);
    }
  };

  // کلاس‌های RTL/LTR برای محتوای اصلی
  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  // بلاک نمایش خطا/لودینگ دسته
  const categoriesBlock = useMemo(() => {
    if (catsLoading) {
      return (
        <div className="mb-4">
          <div className="h-14 animate-pulse bg-white rounded-xl shadow-sm" />
        </div>
      );
    }
    if (catsError) {
      return (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-xl border border-red-100">
          {t("common.error")}: {catsError}
        </div>
      );
    }
    return (
      <ExamCategoryExplorer
        path={path}
        categories={currentCategories}
        onSelect={handleSelectCategory}
        onPathClick={handlePathClick}
      />
    );
  }, [catsLoading, catsError, currentCategories, path, t]);

  // بلاک نمایش لیست آزمون‌ها
  const examsBlock = useMemo(() => {
    if (examsLoading) {
      return (
        <div className="mt-4">
          <div className="h-24 animate-pulse bg-white rounded-xl shadow-sm" />
        </div>
      );
    }
    if (examsError) {
      return (
        <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-xl border border-red-100">
          {t("common.error")}: {examsError}
        </div>
      );
    }
    if (exams.length > 0) {
      return (
        <>
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            {t("exams.list_in_category", {
              category: path[path.length - 1]?.title,
            })}
          </h2>
          <ExamList exams={exams} subscriptions={subscriptions} />
        </>
      );
    }
    if (currentCategories.length === 0) {
      return <EmptyState message={t("exams.empty_state_message")} />;
    }
    return null;
  }, [examsLoading, examsError, exams, currentCategories.length, path, t]);

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("exams.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <h1 className="text-2xl font-bold text-gray-800 mb-6">
            {t("exams.page_header")}
          </h1>

          {categoriesBlock}
          {examsBlock}
        </main>
      </div>
    </div>
  );
};

export default ExamsPage;
